const BITRATES_KBPS_MPEG1 = [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320];
const BITRATES_KBPS_MPEG2 = [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160];
const SAMPLE_RATES = {
    mpeg1: [44100, 48000, 32000],
    mpeg2: [22050, 24000, 16000],
    mpeg25: [11025, 12000, 8000],
};

/** Encoders that write their delay into the header after a Xing/Info tag: LAME, and ffmpeg's libavcodec and libavformat. */
const ENCODER_TAGS_WITH_DELAY = ['LAME', 'Lavc', 'Lavf'];

/** Samples an mp3 decoder holds back before its output lines up with the input. */
const DECODER_DELAY_SAMPLES = 529;

interface FrameHeader {
    isMpeg1: boolean;
    isMono: boolean;
    sampleRate: number;
    samplesPerFrame: number;
    length: number;
}

export interface Mp3FrameIndex {
    sampleRate: number;
    samplesPerFrame: number;
    /** Byte offset of every audio frame, in order. Excludes Xing/Info header frames, which decode to nothing. */
    frameOffsets: number[];
    /** Byte offset just past the last audio frame. */
    dataEnd: number;
    /**
     * Samples a full decode drops from the start: the encoder's delay from a LAME header plus the
     * decoder's own delay. Zero when the file has no LAME header.
     */
    leadingSkipSamples: number;
    /** How long a full decode of the file is, in seconds. */
    durationSeconds: number;
}

export interface Mp3Slice {
    bytes: Uint8Array;
    /** Where, in seconds of the full decode, the first sample of the decoded slice lands. */
    startTime: number;
}

const readFrameHeader = (bytes: Uint8Array, offset: number): FrameHeader | null => {
    if (offset + 4 > bytes.length) {
        return null;
    }
    const b1 = bytes[offset + 1];
    const b2 = bytes[offset + 2];
    const b3 = bytes[offset + 3];
    if (bytes[offset] !== 0xff || (b1 & 0xe0) !== 0xe0) {
        return null;
    }
    const versionBits = (b1 >> 3) & 0b11;
    const layerBits = (b1 >> 1) & 0b11;
    const bitrateIndex = b2 >> 4;
    const sampleRateIndex = (b2 >> 2) & 0b11;
    if (versionBits === 0b01 || layerBits !== 0b01) {
        return null;
    }
    if (bitrateIndex === 0 || bitrateIndex === 0b1111 || sampleRateIndex === 0b11) {
        return null;
    }
    const isMpeg1 = versionBits === 0b11;
    const sampleRates =
        versionBits === 0b11
            ? SAMPLE_RATES.mpeg1
            : versionBits === 0b10
              ? SAMPLE_RATES.mpeg2
              : SAMPLE_RATES.mpeg25;
    const sampleRate = sampleRates[sampleRateIndex];
    const bitrate = (isMpeg1 ? BITRATES_KBPS_MPEG1 : BITRATES_KBPS_MPEG2)[bitrateIndex] * 1000;
    const padding = (b2 >> 1) & 1;
    const samplesPerFrame = isMpeg1 ? 1152 : 576;
    return {
        isMpeg1,
        isMono: b3 >> 6 === 0b11,
        sampleRate,
        samplesPerFrame,
        length: Math.floor(((samplesPerFrame / 8) * bitrate) / sampleRate) + padding,
    };
};

const id3v2Length = (bytes: Uint8Array, offset: number): number => {
    if (
        offset + 10 > bytes.length ||
        bytes[offset] !== 0x49 ||
        bytes[offset + 1] !== 0x44 ||
        bytes[offset + 2] !== 0x33
    ) {
        return 0;
    }
    const size =
        (bytes[offset + 6] << 21) |
        (bytes[offset + 7] << 14) |
        (bytes[offset + 8] << 7) |
        bytes[offset + 9];
    const hasFooter = (bytes[offset + 5] & 0x10) !== 0;
    return 10 + size + (hasFooter ? 10 : 0);
};

const readAscii = (bytes: Uint8Array, offset: number, length: number) =>
    String.fromCharCode(...bytes.subarray(offset, offset + length));

/**
 * Reads the Xing/Info header that LAME writes into the first frame. Returns undefined when the
 * frame is ordinary audio, or the samples a full decode skips when it is the header.
 */
const readInfoFrame = (
    bytes: Uint8Array,
    offset: number,
    header: FrameHeader,
): { leadingSkipSamples: number; endTrimSamples: number } | undefined => {
    const sideInfoLength = header.isMpeg1 ? (header.isMono ? 17 : 32) : header.isMono ? 9 : 17;
    let position = offset + 4 + sideInfoLength;
    const tag = readAscii(bytes, position, 4);
    if (tag !== 'Xing' && tag !== 'Info') {
        return undefined;
    }
    const flags = (bytes[position + 4] << 24) | (bytes[position + 5] << 16);
    const flagsLow = (bytes[position + 6] << 8) | bytes[position + 7];
    const allFlags = flags | flagsLow;
    position += 8;
    position += allFlags & 0x1 ? 4 : 0;
    position += allFlags & 0x2 ? 4 : 0;
    position += allFlags & 0x4 ? 100 : 0;
    position += allFlags & 0x8 ? 4 : 0;
    if (!ENCODER_TAGS_WITH_DELAY.includes(readAscii(bytes, position, 4))) {
        return { leadingSkipSamples: 0, endTrimSamples: 0 };
    }
    const delayOffset = position + 21;
    const encoderDelay = (bytes[delayOffset] << 4) | (bytes[delayOffset + 1] >> 4);
    const endPadding = ((bytes[delayOffset + 1] & 0x0f) << 8) | bytes[delayOffset + 2];
    return {
        leadingSkipSamples: encoderDelay + DECODER_DELAY_SAMPLES,
        endTrimSamples: endPadding - DECODER_DELAY_SAMPLES,
    };
};

/** Whether `header`, read at `offset`, is a whole frame of the same stream as `first`. */
const isFrameOf = (header: FrameHeader, first: FrameHeader, offset: number, bytes: Uint8Array) =>
    header.sampleRate === first.sampleRate &&
    header.samplesPerFrame === first.samplesPerFrame &&
    offset + header.length <= bytes.length;

/**
 * The next place a frame of the stream starts after bytes that are not one, such as a tag or junk
 * stitched in between two recordings. A frame only counts when another frame or tag follows it,
 * since a stray 0xFF byte can look like a frame header.
 */
const findNextFrame = (bytes: Uint8Array, from: number, first: FrameHeader): number | undefined => {
    for (let offset = from; offset < bytes.length; offset++) {
        if (id3v2Length(bytes, offset)) {
            return offset;
        }
        const header = readFrameHeader(bytes, offset);
        if (!header || !isFrameOf(header, first, offset, bytes)) {
            continue;
        }
        const after = offset + header.length;
        const followed =
            after === bytes.length ||
            id3v2Length(bytes, after) > 0 ||
            readFrameHeader(bytes, after) !== null;
        if (followed) {
            return offset;
        }
    }
    return undefined;
};

/**
 * Finds every audio frame in an mp3 file by walking the frame headers, without decoding anything.
 * Skips tags and junk between frames, as a podcast with ads stitched in has. Throws when the bytes
 * are not a Layer III stream.
 */
export const indexMp3Frames = (bytes: Uint8Array): Mp3FrameIndex => {
    let offset = id3v2Length(bytes, 0);
    const first = readFrameHeader(bytes, offset);
    if (!first) {
        throw new Error('Not an mp3 file: no MPEG Layer III frame where the audio should start');
    }
    const infoFrame = readInfoFrame(bytes, offset, first);
    if (infoFrame) {
        offset += first.length;
    }

    const frameOffsets: number[] = [];
    let dataEnd = offset;
    let isContiguous = true;
    while (offset < bytes.length) {
        const tagLength = id3v2Length(bytes, offset);
        const header = tagLength ? null : readFrameHeader(bytes, offset);
        if (tagLength || !header || !isFrameOf(header, first, offset, bytes)) {
            const next = tagLength ? offset + tagLength : findNextFrame(bytes, offset + 1, first);
            if (next === undefined) {
                break;
            }
            isContiguous = false;
            offset = next;
            continue;
        }
        if (!readInfoFrame(bytes, offset, header)) {
            frameOffsets.push(offset);
        }
        offset += header.length;
        dataEnd = offset;
    }
    const endTrimSamples = isContiguous ? (infoFrame?.endTrimSamples ?? 0) : 0;
    const decodedSamples =
        frameOffsets.length * first.samplesPerFrame -
        (infoFrame?.leadingSkipSamples ?? 0) -
        endTrimSamples;
    return {
        sampleRate: first.sampleRate,
        samplesPerFrame: first.samplesPerFrame,
        frameOffsets,
        dataEnd,
        leadingSkipSamples: infoFrame?.leadingSkipSamples ?? 0,
        durationSeconds: decodedSamples / first.sampleRate,
    };
};

/**
 * Cuts out the frames covering `startTime`–`endTime` (seconds of the full decode), starting a few
 * frames early: an mp3 frame can borrow bits from the frames before it, so the first frames of a
 * slice decode wrong and have to be thrown away.
 */
export const sliceMp3 = (
    bytes: Uint8Array,
    index: Mp3FrameIndex,
    startTime: number,
    endTime: number,
    prerollFrames: number,
): Mp3Slice => {
    const { sampleRate, samplesPerFrame, frameOffsets, leadingSkipSamples } = index;
    const frameAt = (time: number) =>
        Math.floor((time * sampleRate + leadingSkipSamples) / samplesPerFrame);
    const finalFrame = frameOffsets.length - 1;
    const lastFrame = Math.min(finalFrame, frameAt(endTime) + 1);
    const firstFrame = Math.max(0, Math.min(lastFrame, frameAt(startTime) - prerollFrames));
    const end = lastFrame + 1 < frameOffsets.length ? frameOffsets[lastFrame + 1] : index.dataEnd;
    return {
        bytes: bytes.subarray(frameOffsets[firstFrame], end),
        startTime: (firstFrame * samplesPerFrame - leadingSkipSamples) / sampleRate,
    };
};
